import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import QRCodeModal from './QRCodeModal';
import { shareOnWhatsApp } from '../utils/qrcode';

const { FiExternalLink, FiCopy, FiTrash2, FiBarChart3, FiCalendar, FiCheck, FiQrCode, FiShare } = FiIcons;

const URLList = ({ urls, onDeleteUrl }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState(null);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(`https://${text}`);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  };

  const handleQRCode = (urlData) => {
    setSelectedUrl(urlData);
    setShowQRModal(true);
  };

  const handleWhatsAppShare = (urlData) => {
    shareOnWhatsApp(urlData.shortUrl, 'Check out this link:');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (urls.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center"
      >
        <div className="text-gray-400 mb-4">
          <SafeIcon icon={FiBarChart3} className="text-6xl mx-auto" />
        </div>
        <h3 className="text-xl font-semibold text-gray-600 mb-2">No URLs shortened yet</h3>
        <p className="text-gray-500">Start by shortening your first URL above!</p>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white rounded-2xl shadow-xl border border-emerald-100"
      >
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
            <SafeIcon icon={FiBarChart3} className="text-emerald-500" />
            <span>Your Shortened URLs ({urls.length})</span>
          </h2>
        </div>

        <div className="divide-y divide-gray-100">
          <AnimatePresence>
            {urls.map((urlData, index) => (
              <motion.div
                key={urlData.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="p-6 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between space-x-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {urlData.originalUrl.replace(/^https?:\/\//, '')}
                      </h3>
                      <motion.a
                        href={urlData.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        className="text-emerald-500 hover:text-emerald-600"
                      >
                        <SafeIcon icon={FiExternalLink} />
                      </motion.a>
                    </div>

                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-2 bg-emerald-50 px-3 py-1 rounded-full">
                        <span className="text-emerald-700 font-mono text-sm">
                          {urlData.shortUrl}
                        </span>
                        <motion.button
                          onClick={() => copyToClipboard(urlData.shortUrl, urlData.id)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <SafeIcon icon={copiedId === urlData.id ? FiCheck : FiCopy} className="text-xs" />
                        </motion.button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} />
                        <span>{formatDate(urlData.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiBarChart3} />
                        <span>{urlData.clicks} clicks</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <motion.button
                        onClick={() => handleQRCode(urlData)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                        title="Generate QR Code"
                      >
                        <SafeIcon icon={FiQrCode} className="text-xs" />
                        <span>QR</span>
                      </motion.button>

                      <motion.button
                        onClick={() => handleWhatsAppShare(urlData)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                        title="Share on WhatsApp"
                      >
                        <SafeIcon icon={FiShare} className="text-xs" />
                        <span>Share</span>
                      </motion.button>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => onDeleteUrl(urlData.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 p-2"
                    title="Delete URL"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </motion.button>
                </div>

                {copiedId === urlData.id && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-emerald-600 text-sm mt-2"
                  >
                    Copied to clipboard!
                  </motion.p>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={selectedUrl?.shortUrl}
        title="QR Code for Short URL"
      />
    </>
  );
};

export default URLList;